<template>
  <div>
    <h1>Manual</h1>
    <ul class="manual-pages">
      <li v-for="m in manualPages" v-bind:key="m.path">
        <router-link :to="{name: 'docs', params: {path: m.path}}">{{m.title}}</router-link>
      </li>
    </ul>

    <template v-if="experimentalManualPages.length > 0">
      <h1>Experimental</h1>
      <ul class="manual-pages">
        <li v-for="m in experimentalManualPages" v-bind:key="m.path">
          <router-link :to="{name: 'docs', params: {path: m.path}}">{{m.title}}</router-link>
        </li>
      </ul>
    </template>

    <div v-if="docsLoaded">
      <h1>Highlights</h1>
      <ul>
        <li v-for='e in highlights' :key="'h'+e.___id">
          <router-link :to="apiLink(e)">{{e.longname}}</router-link>
        </li>
      </ul>

      <h1>Classes</h1>
      <ul>
        <li v-for='e in classes' :key="'c'+e.___id">
          <router-link :to="apiLink(e)">{{e.longname}}</router-link>
        </li>
      </ul>

      <h1>Events</h1>
      <ul>
        <li v-for='e in events' :key="'e'+e.___id">
          <router-link :to="apiLink(e)">{{e.name}}</router-link>
        </li>
      </ul>

      <h1>Interfaces and Types</h1>
      <ul>
        <li v-for='e in interfaces' :key="'i'+e.___id">
          <router-link :to="apiLink(e)">{{e.longname}}</router-link>
        </li>
      </ul>

      <h1>Everything</h1>
      <ul>
        <li v-for='e in globalNavigation' :key="'g'+e.___id">
          <router-link :to="apiLink(e)">{{e.longname}}</router-link>
        </li>
      </ul>

      <template v-if="experimentalApis.length > 0">
        <h1>Experimental APIs</h1>
        <ul>
          <li v-for='e in experimentalApis' :key="'g'+e.___id">
            <router-link :to="apiLink(e)">{{e.longname}}</router-link>
          </li>
        </ul>
      </template>
    </div>
    <div v-else>
      <SpinnerApiLoading/>
    </div>
  </div>
</template>

<script>
import { Docs } from '../utils/docs';
import { getManualPages } from '../utils/manual';
import SpinnerApiLoading from './SpinnerApiLoading';

/**
 * @param {string} text
 * @return {boolean} true if the first character is upper case.
 */
function isCapitalized (text) {
  return text[0] === text[0].toUpperCase();
}

/**
 * Alphabetically compare two ASCII strings. The strings are automatically
 * converted to lower case.
 *
 * Note, that this yields much better performance than e.g. localeCompare.
 *
 * @param {string} text1
 * @param {string} text2
 * @return {number} -1,0,1 classic comparator return value
 */
function alphabeticalCompare (text1, text2) {
  const a = text1.toLowerCase();
  const b = text2.toLowerCase();
  return a < b ? -1 : (a > b ? 1 : 0);
}

/**
 * Sort doc items hierarchically and by `longname`.
 *
 * e.g.:
 *
 * clpp
 * clpp.Player
 * clpp.ads
 * clpp.ads.Thumbs
 * clpp.ads.extra.Metadata
 * clpp.drm
 * clpp.drm.DrmConfiguration
 */
function sort (docItems) {
  return docItems.sort((item1, item2) => {
    const namespaceParts1 = item1.longname.split('.');
    const namespaceParts2 = item2.longname.split('.');
    const length = Math.min(namespaceParts1.length, namespaceParts2.length);

    // Go through namespace parts, skip the parts that are the same
    // and compare them if they differ.
    for (let i = 0; i < length; i++) {
      const part1 = namespaceParts1[i];
      const part2 = namespaceParts2[i];
      if (part1 !== part2) {
        // The one with less namespaces goes first
        // e.g. clpp.ads.Thumbs < clpp.ads.extra.Metadata
        if (isCapitalized(part1) && !isCapitalized(part2)) {
          return -1;
        } else if (!isCapitalized(part1) && isCapitalized(part2)) {
          return 1;
        // If they have the same amount of namespaces, then just sort them
        // alphabetically.
        } else {
          return alphabeticalCompare(part1, part2);
        }
      }
    }

    // If they don't differ in any namespace prefix, so they are part of the
    // same namespace, then the shorter one comes first.
    // e.g. clpp.drm < clpp.drm.DrmConfiguration
    if (namespaceParts1.length !== namespaceParts2.length) {
      return namespaceParts1.length - namespaceParts2.length;
    }

    // If they are the same strings, return 0 (equal)
    return 0;
  });
}

export default {
  name: 'NavigationApi',
  components: { SpinnerApiLoading },
  data: function () {
    return {
      expandApi: false
    };
  },
  watch: {
    $route (to, from) {
      this.checkRoute();
    }
  },
  created () {
    this.checkRoute();
  },
  computed: {
    docsLoaded () {
      return !!this.$store.state.docs.db;
    },
    classes () {
      return sort(this.filter(['class']));
    },
    interfaces () {
      return sort(this.filter(['typedef']));
    },
    events () {
      return sort(this.filter(['event']));
    },
    globalNavigation () {
      return sort(this.filter(['class', 'interface', 'typedef', 'namespace']));
    },
    experimentalApis () {
      return sort(this.filter(['class', 'interface', 'typedef', 'namespace'], { experimental: true }));
    },
    manualPages () {
      return getManualPages().filter(p => p.hidden !== true);
    },
    experimentalManualPages () {
      return this.manualPages.filter(p => p.experimental);
    },
    highlights () {
      const h = [];
      const db = this.$store.state.docs.db;
      if (!db) return [];
      const add = (values) => values.forEach(v => h.push(v));
      const find = (spec) => db(spec).get().filter(v => !!v);

      add(find({ longname: 'clpp.init' }));
      add(find({ longname: 'clpp.Player' }));
      add(find({ longname: 'clpp.PlayerConfiguration' }));
      add(find({ longname: 'clpp.Source' }));
      add(find({ longname: 'clpp.events' }));

      return h;
    }
  },
  methods: {
    checkRoute () {
      if (this.$route.name === 'api') {
        this.expandApi = true;
      }
    },

    apiLink (entry) {
      return Docs.linkTarget(entry);
    },
    eventName (e) {
      let value = null;
      if (e && e.meta && e.meta.code) {
        value = e.meta.code.value;
      }

      let name = e.longname.replace('.event:', '.');
      if (value) {
        name = name + ' (' + value + ')';
      }
      return name;
    },
    filter (kinds, options = {}) {
      const db = this.$store.state.docs.db;
      if (!db) return [];

      let members = db({ kind: kinds }).get()
        .filter(m => !m.hidden);

      if (options.experimental) {
        members = members.filter(m => m.experimental === true);
      } else {
        members = members.filter(m => !m.experimental);
      }

      return members.sort((a, b) => {
        const partsA = a.longname.split('.');
        const partsB = b.longname.split('.');
        if (partsA.length !== partsB.length) {
          return partsA.length - partsB.length;
        }
        return a.longname.toLowerCase().localeCompare(b.longname.toLowerCase());
      });
    }
  }
};
</script>
